
import React, { Component, ReactNode } from 'react';
import { createDragLogger } from '@/utils/dragStateUtils';

interface DragErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  context?: 'drag_operation' | 'ghost_card' | 'general'; // **PHASE 2: Add context**
}

interface DragErrorBoundaryState {
  hasError: boolean;
  errorCount: number;
  errorContext?: string; // **PHASE 2: Track error context**
  lastErrorTime?: number; // **PHASE 2: Track timing**
}

const logger = createDragLogger('DragErrorBoundary');

export class DragErrorBoundary extends Component<DragErrorBoundaryProps, DragErrorBoundaryState> {
  private errorTimeout: NodeJS.Timeout | null = null;

  constructor(props: DragErrorBoundaryProps) {
    super(props);
    this.state = { 
      hasError: false, 
      errorCount: 0,
      errorContext: undefined,
      lastErrorTime: undefined
    };
  }

  static getDerivedStateFromError(error: Error): Partial<DragErrorBoundaryState> {
    // **PHASE 2: Enhanced error state tracking**
    return { 
      hasError: true, 
      errorCount: 0,
      lastErrorTime: Date.now()
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const now = Date.now();
    const timeSinceLastError = this.state.lastErrorTime ? now - this.state.lastErrorTime : 0;
    
    // **PHASE 2: Detect if this might be a normal drag termination vs genuine error**
    const isDragTerminationError = error.message?.includes('drag') && 
                                   error.message?.includes('termination') ||
                                   timeSinceLastError < 1000; // Very recent error might be termination-related

    const contextualErrorType = isDragTerminationError ? 'drag_termination' : 'drag_error';
    
    logger.error('Drag operation error caught', error, {
      componentStack: errorInfo.componentStack,
      errorBoundary: 'DragErrorBoundary',
      context: this.props.context,
      errorType: contextualErrorType,
      timeSinceLastError,
      isDragTerminationError
    });

    // **PHASE 2: Update state with context**
    this.setState({ 
      errorContext: contextualErrorType,
      lastErrorTime: now
    });

    // **PHASE 2: Faster recovery for drag termination errors**
    const recoveryDelay = isDragTerminationError ? 500 : 2000;

    if (this.errorTimeout) {
      clearTimeout(this.errorTimeout);
    }

    this.errorTimeout = setTimeout(() => {
      this.setState({ 
        hasError: false, 
        errorCount: 0, 
        errorContext: undefined,
        lastErrorTime: undefined
      });
      logger.info(`Error boundary reset after ${recoveryDelay}ms - attempting recovery`, {
        errorType: contextualErrorType
      });
    }, recoveryDelay);
  }

  componentWillUnmount() {
    if (this.errorTimeout) {
      clearTimeout(this.errorTimeout);
    }
  }

  render() {
    if (this.state.hasError) {
      // **PHASE 2: Contextual error messages**
      const getErrorMessage = () => {
        if (this.state.errorContext === 'drag_termination') {
          return 'Drag operation completing...';
        }
        
        switch (this.props.context) {
          case 'ghost_card':
            return 'Ghost card temporarily unavailable';
          case 'drag_operation':
            return 'Drag operation interrupted';
          default:
            return 'Drag operation interrupted';
        }
      };

      // **PHASE 2: Less jarring visual for termination errors**
      const isTermination = this.state.errorContext === 'drag_termination';
      
      return this.props.fallback || (
        <div className={`drag-error-fallback text-xs p-2 transition-opacity duration-300 ${
          isTermination ? 'text-gray-400 opacity-70' : 'text-gray-500'
        }`}>
          {getErrorMessage()}
          {isTermination && (
            <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse inline-block ml-2" />
          )}
        </div>
      );
    }

    return this.props.children;
  }
}
